import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import app from '../../main' // import the instance
import checkResponse from '@/utils/checkResponse'
import parserFilters from '@/utils/parserFilters'
import parserSlug from '@/utils/parserSlug'
import moment from 'moment'

Vue.use(Vuex)

const state = {
  toQuotes: [],
  employees: [],
  customer: '',
  project: {},
  pagination: {
    currentPage: 1,
    perPage: 5,
    totalToQuotes: 0,
    totalPages: 0
  },
  toQuotesCatalog: {
    customers: []
  },
  filterParams: '',
  isModalVisible: false,
  isModalQuotationVisible: false,
  isModalEmployeeVisible: false
}

const mutations = {
  SET_TO_QUOTES (state, payload) {
    state.toQuotes = payload.data

    state.pagination = {
      currentPage: payload.current_page,
      perPage: payload.per_page,
      totalToQuotes: payload.total,
      totalPages: payload.last_page
    }
  },
  ADD_TO_QUOTE (state, payload) {
    state.toQuotes.unshift(payload)
  },
  UPDATE_TO_QUOTE (state, payload) {
    const idx = state.toQuotes.findIndex(u => u.id === payload.id)

    if (idx >= 0) {
      Vue.set(state.toQuotes, idx, payload)
    }
  },
  UPDATE_TO_QUOTE_STAGE_CHANGE (state, payload) {
    const idx = state.toQuotes.findIndex(u => u.id === payload.id)

    if (idx >= 0) {
      Vue.set(state.toQuotes, idx, payload)
    }

    setTimeout(() => { Vue.delete(state.toQuotes, idx) }, 3000)
  },
  DELETE_TO_QUOTE (state, payload) {
    const idx = state.toQuotes.findIndex(u => u.id === payload.id)

    if (idx >= 0) {
      Vue.delete(state.toQuotes, idx)
    }
  },
  DELETE_TO_QUOTES (state, payload) {
    for (let id of payload.ids) {
      const idx = state.toQuotes.findIndex(u => u.id === id)

      if (idx >= 0) {
        Vue.delete(state.toQuotes, idx)
      }
    }
  },
  SET_PROJECT (state, payload) {
    state.project = payload.data
  },
  SET_EMPLOYEES (state, payload) {
    state.employees = [...payload.data]
  },
  SET_MODAL_VISIBLE (state, payload) {
    state.isModalVisible = payload
  },
  SET_MODAL_QUOTATION_VISIBLE (state, payload) {
    state.isModalQuotationVisible = payload
  },
  SET_MODAL_EMPLOYEE_VISIBLE (state, payload) {
    state.isModalEmployeeVisible = payload
  },
  SET_FILTER_PARAMS (state, payload) {
    state.filterParams = payload
  },
  SET_CUSTOMER (state, { object, payload }) {
    state.customer = payload.data[0]
  },
  SET_TO_QUOTES_CATALOG (state, payload) {
    state.toQuotesCatalog = { ...state.toQuotesCatalog, ...payload }
  }
}

const actions = {
  async loadToQuotes ({ commit }, payload) {
    app.$Progress.start()
    try {
      let customer = payload.customer ? `&customer_id=${payload.customer}` : ''
      const response = await axios.get(`to-quotes?page=${payload.page}${state.filterParams}${customer}&stage=1`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_TO_QUOTES', response.data)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async loadProject ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.get(`projects/${payload.id}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'strings.projects', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_PROJECT', response.data)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async loadEmployees ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.get(`employees?&page=1&employer_id=${payload.employer}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'strings.employees', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_EMPLOYEES', response.data)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async addToQuote ({ commit }, payload) {
    const toQuote = {
      project_name: payload.project_name,
      location: payload.location,
      customer_id: payload.customer.id,
      project_created_on: moment(payload.project_created_on).format('YYYY-MM-DD'),
      quotation_delivery_date: moment(payload.quotation_delivery_date).format('YYYY-MM-DD'),
      stage: 1,
      status: 1,
      additional: payload.additional ? parseInt(payload.additional) : 0,
      additional_id: parseInt(payload.additional_id)
    }

    app.$Progress.start()

    try {
      const response = await axios.post('projects', toQuote)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('ADD_TO_QUOTE', response.data)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: 'front.added_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async addToQuoteAdditional ({ commit }, payload) {
    const toQuote = {
      project_name: payload.project_name,
      location: payload.location,
      customer_id: payload.customer.id,
      project_created_on: moment(payload.project_created_on).format('YYYY-MM-DD'),
      quotation_delivery_date: moment(payload.quotation_delivery_date).format('YYYY-MM-DD'),
      stage: 1,
      status: 1,
      additional: parseInt(payload.additional),
      additional_id: parseInt(payload.additional_id)
    }

    app.$Progress.start()

    try {
      const response = await axios.post('additionals', toQuote)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('ADD_TO_QUOTE', response.data)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: 'front.added_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },

  async editToQuote ({ commit }, { payload, date }) {
    const toQuote = {
      project_name: payload.project_name,
      location: payload.location,
      customer_id: payload.customer.id,
      project_created_on: moment(payload.project_created_on).format('YYYY-MM-DD'),
      stage: payload.stage,
      status: payload.stage,
      additional: payload.additional,
      docs_date_recived: moment(date).format('YYYY-MM-DD')
    }

    app.$Progress.start()

    try {
      const response = await axios.put(`projects/${payload.id}`, toQuote)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_TO_QUOTE', response.data)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async editQuote ({ commit }, payload) {
    const quote = {
      project_name: payload.project_name,
      location: payload.location,
      customer_id: payload.customer.id,
      project_created_on: moment(payload.project_created_on).format('YYYY-MM-DD'),
      stage: payload.stage,
      status: payload.status,
      additional: payload.additional,
      docs_date_recived: moment(payload.docs_date_recived).format('YYYY-MM-DD'),
      comments_quotation: payload.comments_quotation,
      currency: payload.currency,
      quotation_delivery_date: moment(payload.quotation_delivery_date).format('YYYY-MM-DD'),
      quotation_start_date: moment(payload.quotation_start_date).format('YYYY-MM-DD'),
      project_created_by: payload.project_created_by,
      quotation_created_by: payload.quotation_created_by

    }

    app.$Progress.start()

    try {
      const response = await axios.put(`projects/${payload.id}`, quote)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('Quotes/UPDATE_QUOTE', response.data, { root: true })
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async setDocsDateRecived ({ commit }, { payload, date }) {
    const toQuote = {
      docs_date_recived: moment(date).format('YYYY-MM-DD')
    }

    app.$Progress.start()

    try {
      const response = await axios.put(`to-quotes/${payload.id}/docs-date-recived`, toQuote)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_TO_QUOTE', response.data)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async addQuotation ({ commit }, payload) {
    const toQuote = {
      quotation_start_date: payload.quotation_start_date && moment(payload.quotation_start_date).format('YYYY-MM-DD'),
      quotation_delivery_date: payload.quotation_delivery_date && moment(payload.quotation_delivery_date).format('YYYY-MM-DD'),
      comments_quotation: payload.comments_quotation
    }

    app.$Progress.start()

    try {
      const response = await axios.put(`quotations/${payload.id}`, toQuote)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_TO_QUOTE_STAGE_CHANGE', response.data)
        commit('SET_MODAL_QUOTATION_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      setTimeout(() => {
        app.$router.push({ name: 'quotes' })
      }, 300)
    }
  },
  async deleteToQuote ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.delete(`projects/${payload.id}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('DELETE_TO_QUOTE', payload)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: 'front.deleted_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async deleteToQuotes ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.delete(`to-quotes/delete`, { params: { ids: payload.ids } })
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('DELETE_TO_QUOTES', payload)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: 'front.deleted_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async actionToQuote ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.put(`projects/${payload.id}/${payload.action}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_TO_QUOTE', response.data)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: `projects.${payload.action}`, icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  printReport ({ commit }, payload) {
    app.$Progress.start()
    try {
      window.open(axios.defaults.baseURL + '/to-quotes/export?' + state.filterParams)
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  applyFilter ({ commit }, payload) {
    const filter = parserFilters(payload)
    commit('SET_FILTER_PARAMS', filter)
  },
  clearFilter ({ commit }, payload) {
    const filter = ''
    commit('SET_FILTER_PARAMS', filter)
  },
  async loadCustomer ({ commit }, payload) {
    let url = parserSlug(payload.url)
    app.$Progress.start()
    try {
      const response = await axios.get(`${url}?${payload.filter}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_CUSTOMER', { object: payload.url, payload: response.data })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async loadToQuotesCatalog ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.get(`projects/catalog`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'strings.projects', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_TO_QUOTES_CATALOG', response.data)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async loadCatalog ({ commit }, payload) {
    let url = parserSlug(payload.url)
    app.$Progress.start()
    try {
      const response = await axios.get(`${url}${payload.filter}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'strings.in_orders', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_TO_QUOTES_CATALOG', { object: payload.url, payload: response.data })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async saveEmployees ({ commit }, payload) {
    app.$Progress.start()

    try {
      const response = await axios.post(`projects/${payload.id}/select-employees`, payload)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_MODAL_EMPLOYEE_VISIBLE', false)
        commit('UPDATE_TO_QUOTE', response.data)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.projects', text: 'front.added_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  setModalVisible ({ commit }, payload) {
    commit('SET_MODAL_VISIBLE', payload)
  },
  setModalQuotationVisible ({ commit }, payload) {
    commit('SET_MODAL_QUOTATION_VISIBLE', payload)
  },
  setModalEmployeeVisible ({ commit }, payload) {
    commit('SET_MODAL_EMPLOYEE_VISIBLE', payload)
  },
  setModalContactVisible ({ commit, dispatch, getters }, payload) {
    dispatch('ProjectContacts/setModalContactVisible', { payload }, { root: true })
  },
  setModalImportVisible ({ commit }, payload) {
    commit('SET_MODAL_IMPORT_VISIBLE', payload, { root: true })
  }
}

const getters = {
  toQuotes: state => state.toQuotes,
  project: state => state.project,
  employees: state => state.employees,
  customer: state => state.customer,
  toQuotesCatalog: state => state.toQuotesCatalog,
  pagination: state => state.pagination,
  isModalVisible: state => state.isModalVisible,
  isModalQuotationVisible: state => state.isModalQuotationVisible,
  isModalEmployeeVisible: state => state.isModalEmployeeVisible,
  isModalContactVisible: (state, getters, rootState, rootGetters) => rootGetters['ProjectContacts/isModalContactVisible'],
  filterParams: state => state.filterParams
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
